import { useMemo } from 'react';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import type { Filter, FilterOp } from 'common/types/summaryDefinitions';
import { getValueType } from 'common/types/summaryDefinitions';
import type { DisplayFormatType } from 'common/types/summaryDefinitions/valueTypes/displayFormats';
import type { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';
import { Button, IconButton, Input } from 'components/ui';
import {
  getDisplayFormatTypeForColumn,
  getFilterOpsForColumnType,
  isOpValidForFormatType,
  validateFilterValue,
} from 'pages/Firms/pages/FirmSummary/filterValidation';
import type { DashboardFieldOption } from '../dashboardEditFields.types';
import {
  DashboardControlRow,
  DashboardControlStack,
  DashboardEmptyHint,
  DashboardSortableRowRoot,
} from './dashboardControls.styles';
import DashboardEnumSelect from './DashboardEnumSelect';

interface DashboardFiltersFieldProps {
  disabled?: boolean;
  onChange: (next: Filter[]) => void;
  options: readonly DashboardFieldOption[];
  testId?: string;
  value: readonly Filter[];
}

const BOOLEAN_OPTIONS: DashboardFieldOption[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

// Filters are unordered — the backend ANDs them — so unlike sort keys these rows are not draggable.
// The row shell is reused for its border and spacing, with the drag column left empty.
const DashboardFiltersField = ({
  disabled = false,
  onChange,
  options,
  testId,
  value,
}: DashboardFiltersFieldProps) => {
  // Same rule as the sort field: never seed a filter row with a column the user cannot use.
  const seedOption = options.find(option => !option.disabledReason);
  // filterValidation keys off the value-type registry, which does not carry every dashboard column.
  // getDisplayFormatTypeForColumn already falls back to 'text' for a miss, so an unmapped column
  // simply gets the permissive operator set rather than breaking.
  const valueTypeById = useMemo(
    () =>
      new Map<string, ValueType>(
        options
          .map(option => [option.value, getValueType(option.value)] as const)
          .filter((entry): entry is readonly [string, ValueType] => Boolean(entry[1]))
      ),
    [options]
  );

  const update = (index: number, next: Filter) =>
    onChange(value.map((entry, i) => (i === index ? next : entry)));

  const handleColumnChange = (index: number, column: string) => {
    const [, [operator, filterValue]] = value[index];
    const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
    const validOps = getFilterOpsForColumnType(formatType);
    // Keep the operator when it still applies, otherwise fall back to the first valid one, and drop
    // a value the new column's format would reject — the same repair the spreadsheet editor does.
    const nextOperator = isOpValidForFormatType(operator, formatType) ? operator : validOps[0].value;
    const keepsValue = validateFilterValue(filterValue, formatType).valid;

    update(index, [column, [nextOperator, keepsValue ? filterValue : '']]);
  };

  const renderValueInput = (index: number, formatType: DisplayFormatType) => {
    const [column, [operator, filterValue]] = value[index];

    if (formatType === 'boolean') {
      return (
        <DashboardEnumSelect
          disabled={disabled}
          isRequired
          onChange={next => next && update(index, [column, [operator, next]])}
          options={BOOLEAN_OPTIONS}
          value={String(filterValue)}
        />
      );
    }

    const { error, valid } = validateFilterValue(filterValue, formatType);

    return (
      <Input
        disabled={disabled}
        error={!valid}
        helperText={valid ? undefined : error}
        onChange={event => update(index, [column, [operator, event.target.value]])}
        placeholder="Value"
        size="small"
        type={formatType === 'date' ? 'date' : 'text'}
        value={String(filterValue ?? '')}
        visualVariant="compactOutlined"
      />
    );
  };

  return (
    <DashboardControlStack>
      {value.length === 0 && <DashboardEmptyHint>No filters applied.</DashboardEmptyHint>}
      {value.map(([column, [operator]], index) => {
        const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);

        return (
          // eslint-disable-next-line react/no-array-index-key
          <DashboardSortableRowRoot key={`filter-${index}`}>
            <span />
            <DashboardControlRow>
              <DashboardEnumSelect
                disabled={disabled}
                isRequired
                onChange={next => next && handleColumnChange(index, next)}
                options={options}
                searchable={options.length > 10}
                value={column}
              />
              <DashboardEnumSelect
                disabled={disabled}
                isRequired
                onChange={next => next && update(index, [column, [next as FilterOp, value[index][1][1]]])}
                options={getFilterOpsForColumnType(formatType).map(option => ({
                  label: option.label,
                  value: option.value,
                }))}
                value={operator}
              />
              {renderValueInput(index, formatType)}
            </DashboardControlRow>
            <IconButton
              aria-label="Remove filter"
              disabled={disabled}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
              size="small">
              <Close fontSize="inherit" />
            </IconButton>
          </DashboardSortableRowRoot>
        );
      })}
      <div>
        <Button
          data-testid={testId}
          disabled={disabled || !seedOption}
          onClick={() => seedOption && onChange([...value, [seedOption.value, ['=', '']]])}
          sizeVariant="compact"
          startIcon={<Add fontSize="inherit" />}>
          Add filter
        </Button>
      </div>
    </DashboardControlStack>
  );
};

export default DashboardFiltersField;
